/**
 * HA-663 — what the staff picker should say when it has no buttons to draw.
 *
 * Pure and separate from the component because the interesting part is a three-way decision over
 * two lists, and the bug it replaces was invisible: the picker filtered to checked-in staff, found
 * none, and rendered an empty <div>. A blank panel is indistinguishable from a broken one, and the
 * operator's next move differs completely depending on WHY it is blank.
 */

/** Mirrors the server's shift status; only the checked-in one matters here. */
const CHECKED_IN = "CheckedIn";

export type StaffPickerState = "ready" | "no-staff" | "nobody-checked-in";

/**
 * The staff this picker will actually draw.
 *
 * Exported so the component and the state below cannot disagree about who counts as on shift —
 * two copies of this filter is how a picker ends up showing "nobody is checked in" above a row of
 * buttons.
 */
export function checkedInStaff<
	TStaff extends { id: string },
	TShift extends { staffMemberId: string; status: string },
>(staffMembers: TStaff[], shifts: TShift[]): TStaff[] {
	return staffMembers.filter((staff) =>
		shifts.some(
			(shift) =>
				shift.staffMemberId === staff.id && shift.status === CHECKED_IN,
		),
	);
}

/**
 * Which of the three situations the picker is in.
 *
 * <b>"No staff at all" and "none checked in" are different problems and must not share a message.</b>
 * The second is the ordinary start of a day and the Check In/Out button above the picker fixes it.
 * The first is a setup gap — there is nobody to check in — and sending that operator to Check In/Out
 * wastes their time on a screen that cannot help.
 */
export function staffPickerState<
	TStaff extends { id: string },
	TShift extends { staffMemberId: string; status: string },
>(staffMembers: TStaff[], shifts: TShift[]): StaffPickerState {
	if (staffMembers.length === 0) return "no-staff";

	return checkedInStaff(staffMembers, shifts).length === 0
		? "nobody-checked-in"
		: "ready";
}

export const STAFF_PICKER_COPY: Record<
	Exclude<StaffPickerState, "ready">,
	{ title: string; detail: string }
> = {
	"no-staff": {
		title: "No staff at this venue yet — nobody to check in",
		detail: "Add staff in Venue Management, then refresh.",
	},
	"nobody-checked-in": {
		title: "Nobody is checked in — use Check In/Out above",
		detail:
			"A check-in can take a moment to appear here. This refreshes on its own, or use Refresh.",
	},
};
